import React, { useRef, useState } from 'react';

// Swipe-to-delete wrapper (Joist-style): drag the card left to reveal a red
// Delete button behind it; tapping that button fires onDelete (which opens
// the Confirm Deletion dialog). Vertical scrolling is untouched — the drag
// only engages once the gesture is clearly horizontal.
const REVEAL = 96; // px of red button revealed when open

export default function SwipeToDelete({ onDelete, children }) {
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef(null);        // { x, y, base, horizontal }
  const suppressClick = useRef(false);

  const onPointerDown = (e) => {
    start.current = { x: e.clientX, y: e.clientY, base: offset, horizontal: null };
    setDragging(true);
  };

  const onPointerMove = (e) => {
    const s = start.current;
    if (!s) return;
    const dx = e.clientX - s.x;
    const dy = e.clientY - s.y;
    if (s.horizontal === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return; // not decided yet
      s.horizontal = Math.abs(dx) > Math.abs(dy);
      if (s.horizontal) {
        try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
      }
    }
    if (!s.horizontal) return;
    setOffset(Math.max(-REVEAL, Math.min(REVEAL, s.base + dx)));
  };

  const endDrag = () => {
    const s = start.current;
    if (!s) return;
    start.current = null;
    setDragging(false);
    if (s.horizontal) suppressClick.current = true; // don't let the drag count as a tap
    // Settle open on the side it was pulled towards, or shut.
    setOffset(o => (o < -REVEAL / 2 ? -REVEAL : o > REVEAL / 2 ? REVEAL : 0));
  };

  return (
    <div className="swipe-wrap">
      <div
        className={`swipe-del${offset > 0 ? ' left' : ''}`}
        role="button"
        onClick={() => { setOffset(0); onDelete(); }}
      >Delete</div>
      <div
        className="swipe-content"
        style={{ transform: `translateX(${offset}px)`, transition: dragging ? 'none' : 'transform .2s ease' }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onClickCapture={(e) => {
          if (suppressClick.current) {
            suppressClick.current = false;
            e.preventDefault();
            e.stopPropagation();
          } else if (offset !== 0) {
            // tap anywhere on an open card closes it
            setOffset(0);
          }
        }}
      >
        {children}
      </div>
    </div>
  );
}
